import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useSalesAnalytics } from "@/hooks/useSalesAnalytics";
import { useEmployees } from "@/hooks/useEmployees";
import { 
  Users, 
  TrendingUp, 
  DollarSign, 
  Trophy,
  Target,
  Activity,
  Zap,
  Award
} from "lucide-react";

interface EmployeePerformanceOverviewProps {
  timeFilter: string;
  departmentFilter: string;
}

export const EmployeePerformanceOverview = ({ timeFilter, departmentFilter }: EmployeePerformanceOverviewProps) => {
  const { data: salesData, isLoading: salesLoading } = useSalesAnalytics();
  const { employees, loading: employeesLoading } = useEmployees();

  if (salesLoading || employeesLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {[...Array(8)].map((_, i) => (
          <Card key={i} className="bg-white/80 backdrop-blur-sm border-slate-200/60">
            <CardContent className="p-6">
              <div className="animate-pulse space-y-3">
                <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                <div className="h-8 bg-gray-200 rounded w-3/4"></div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  const totalEmployees = employees?.length || 0;
  const activeEmployees = employees?.filter(emp => emp.status === 'present').length || 0;
  const totalRevenue = salesData?.totalRevenue || 0;
  const averageRevenuePerEmployee = totalEmployees > 0 ? totalRevenue / totalEmployees : 0;
  const topPerformer = salesData?.topEmployees?.[0];
  const performanceGrowth = 15.2; // Mock data - في التطبيق الحقيقي يجب حسابها من البيانات
  const completionRate = 87.5; // Mock data
  const customerSatisfaction = 92.3; // Mock data

  const overviewCards = [
    {
      title: "إجمالي الموظفين",
      value: totalEmployees.toString(),
      icon: Users,
      color: "from-blue-600 to-blue-500",
      textColor: "text-blue-100",
      bgColor: "text-white"
    },
    {
      title: "الموظفين النشطين",
      value: activeEmployees.toString(),
      icon: Activity,
      color: "from-green-600 to-green-500",
      textColor: "text-green-100",
      bgColor: "text-white"
    },
    {
      title: "إجمالي الإيرادات",
      value: `${totalRevenue.toLocaleString('ar-SA')} ر.س`,
      icon: DollarSign,
      color: "from-emerald-600 to-emerald-500",
      textColor: "text-emerald-100",
      bgColor: "text-white"
    },
    {
      title: "متوسط الإيراد/موظف",
      value: `${averageRevenuePerEmployee.toLocaleString('ar-SA', { maximumFractionDigits: 0 })} ر.س`,
      icon: TrendingUp,
      color: "from-purple-600 to-purple-500",
      textColor: "text-purple-100",
      bgColor: "text-white"
    },
    {
      title: "أفضل موظف",
      value: topPerformer?.name || "غير محدد",
      icon: Trophy,
      color: "from-yellow-600 to-yellow-500",
      textColor: "text-yellow-100",
      bgColor: "text-white"
    },
    {
      title: "نمو الأداء",
      value: `+${performanceGrowth}%`,
      icon: Zap,
      color: "from-indigo-600 to-indigo-500",
      textColor: "text-indigo-100",
      bgColor: "text-white"
    },
    {
      title: "معدل الإنجاز",
      value: `${completionRate}%`,
      icon: Target,
      color: "from-pink-600 to-pink-500",
      textColor: "text-pink-100",
      bgColor: "text-white"
    },
    {
      title: "رضا العملاء",
      value: `${customerSatisfaction}%`,
      icon: Award,
      color: "from-cyan-600 to-cyan-500",
      textColor: "text-cyan-100",
      bgColor: "text-white"
    }
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      {overviewCards.map((card, index) => (
        <Card key={index} className={`bg-gradient-to-r ${card.color} ${card.bgColor} transform hover:scale-105 transition-all duration-200 shadow-lg hover:shadow-xl`}>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className={`${card.textColor} text-sm font-medium`}>{card.title}</p>
                <p className="text-2xl font-bold mt-1">{card.value}</p>
                {index === 4 && topPerformer && (
                  <p className={`${card.textColor} text-xs mt-1`}>
                    {topPerformer.total.toLocaleString('ar-SA')} ر.س
                  </p>
                )}
              </div>
              <div className={`p-3 bg-white/20 rounded-lg`}>
                <card.icon className="w-6 h-6" />
              </div>
            </div>
            
            {/* إضافة مؤشر التغيير للبطاقات المناسبة */}
            {(index === 5 || index === 6 || index === 7) && (
              <div className="mt-3 pt-3 border-t border-white/20">
                <div className="flex items-center justify-between text-sm">
                  <span className={card.textColor}>من الشهر الماضي</span>
                  <Badge className="bg-white/20 text-white">
                    +{Math.random() * 10 + 5}%
                  </Badge>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
};